import React, { useCallback, useEffect, useRef, useState } from "react";
import { AttachmentIcon, InfoIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertIcon,
  Button,
  Checkbox,
  Code,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { Get, Post } from "api";
import { useDropzone } from "react-dropzone";
import { useParams } from "react-router-dom";
import Papa from "papaparse";
import moment from "moment";

type AppDetailRouteParams = {
  appId: string;
  partnerId: string;
};

const EXAMPLE_UUIDS = [
  "4a9ce06e-7c44-49eb-9d74-da3487ef6af9",
  "ae8d8780-e790-425e-b897-4f2d32151645",
  "f9f7b4d7-2b0e-4e8b-8e4b-5c4b6f4b2b8f",
  "a8b4d7e8-4b0e-4e8b-8e4b-5c4b6f4b2b8f",
];

interface UploadSuppressionListViewProps {
  appName: string;
}

interface SuppressionList {
  filename: string;
  name: string;
  partnerId: number;
  status: string;
  storeId: string;
  createdAt: string;
}

const isUUID = (string: string) => {
  const uuidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return uuidRegex.test(string);
};

const UploadSuppressionListView = ({
  appName,
}: UploadSuppressionListViewProps) => {
  const params = useParams<AppDetailRouteParams>();

  const toast = useToast();

  const [file, setFile] = useState<File | null>(null);
  const [suppressions, setSuppressions] = useState<SuppressionList[]>([]);
  const [suppressionsLoading, setSuppressionsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isFileEncoded, setIsFileEncoded] = useState(false);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationProgress, setValidationProgress] = useState(0);
  const progressRef = useRef(0);

  const [xhrInstance, setXhrInstance] = useState<XMLHttpRequest | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const getIDs = (): Array<number | string | null> | null => {
    const parsedPartnerId = parseInt(params.partnerId, 10);
    const appId = params.appId;

    if (isNaN(parsedPartnerId) || !parsedPartnerId || !appId) {
      return null;
    }

    return [parsedPartnerId, appId];
  };

  const [partnerId, appId] = getIDs();

  const getSuppressionList = useCallback(async () => {
    setSuppressionsLoading(true);
    try {
      const response = (await Get(
        `/manage/partners/${partnerId}/suppression-lists`
      )) as SuppressionList[];
      setSuppressions(
        response
          .filter((sup) => sup.storeId === appId)
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
      );
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to fetch suppression list",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setSuppressionsLoading(false);
    }
  }, [toast, partnerId, appId]);

  useEffect(() => {
    if (!partnerId) {
      return;
    }

    getSuppressionList();
  }, []);

  const validateFile = useCallback(async (file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      let isValid = true;
      let rowCount = 0;
      const batchSize = 100000;
      const fileSize = file.size;
      let bytesProcessed = 0;
      let invalidUUIDcount = 0;
      let isFirstRow = true;

      const parseConfig = {
        header: false,
        worker: true,
        chunkSize: batchSize,
        chunk: (results: any, parser: any) => {
          rowCount += results.data.length;
          bytesProcessed += results.meta.cursor - bytesProcessed;
          const progress = Math.min(
            Math.round((bytesProcessed / fileSize) * 100),
            100
          );
          progressRef.current = progress;

          if (progress % 1 === 0) {
            setValidationProgress(progress);
          }

          for (const row of results.data) {
            if (isFirstRow) {
              isFirstRow = false;
              continue;
            }

            if (row.length !== 1) {
              isValid = false;
              parser.abort();
              reject(
                new Error(
                  `The list must have only one column. Invalid row: ${rowCount}`
                )
              );
              return;
            }

            if (!isUUID(row[0])) {
              invalidUUIDcount++;
            }
          }
        },
        complete: () => {
          setValidationProgress(100);
          if (invalidUUIDcount > (rowCount - 1) / 100) {
            setIsFileEncoded(true);
          }
          if (isValid) {
            resolve();
          }
        },
        error: (error: any) => {
          console.error("Error during parsing:", error);
          reject(new Error("Error reading file."));
        },
      };

      Papa.parse(file, parseConfig);
    });
  }, []);

  const handleUpload = useCallback(
    async (acceptedFile: File, isEncoded: boolean) => {
      try {
        setIsUploading(true);
        setUploadProgress(0);
        const response = await Post(
          `/manage/partners/${partnerId}/suppression-lists`,
          {
            name: appName,
            storeId: appId,
            filename: acceptedFile.name,
            isSha256: isEncoded,
          }
        );

        if (response.err) {
          toast({
            title: "Error",
            description: response.err,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
          return;
        }

        const uploadLink = response.link;

        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          setXhrInstance(xhr);
          xhr.open("PUT", uploadLink, true);
          xhr.setRequestHeader("Content-Type", acceptedFile.type);

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const percentComplete = (event.loaded / event.total) * 100;
              setUploadProgress(Math.round(percentComplete));
            }
          };

          xhr.onload = () => {
            if (xhr.status === 200) {
              resolve(xhr.response);
            } else {
              reject(new Error("Failed to upload the file"));
            }
          };

          xhr.onerror = () => {
            reject(new Error("Failed to upload the file"));
          };

          xhr.send(acceptedFile);
        });

        toast({
          title: "Success",
          description: "Suppression list uploaded successfully",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        onClose();
        getSuppressionList();
      } catch (error) {
        console.error(error);
        toast({
          title: "Error",
          description: "Failed to upload the file",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsUploading(false);
        setFile(null);
        setUploadProgress(0);
        setValidationProgress(0);
        setIsValidating(false);
        setIsFileEncoded(false);
        setXhrInstance(null);
      }
    },
    [toast, partnerId, appId, appName, onClose, getSuppressionList]
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");

      if (!isCSV) {
        toast({
          title: "Error",
          description: "Please upload a CSV file",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        return;
      }

      try {
        setIsValidating(true);
        await validateFile(file);

        setFile(file);
      } catch (error) {
        toast({
          title: "Error",
          description: (error as Error).message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsValidating(false);
      }
    },
    [toast]
  );

  const handleCancel = useCallback(() => {
    if (isValidating) {
      return;
    }

    if (xhrInstance) {
      xhrInstance.abort();
      setXhrInstance(null);
      toast({
        title: "Cancelled",
        description: "File upload cancelled",
        status: "warning",
        duration: 9000,
        isClosable: true,
      });
    }

    setIsUploading(false);
    setIsValidating(false);
    setIsFileEncoded(false);
    setValidationProgress(0);
    setUploadProgress(0);

    setFile(null);
    onClose();
  }, [isValidating, xhrInstance, onClose]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  const renderUploadView = useCallback(() => {
    return (
      <Flex
        {...(file || isValidating || isUploading ? {} : getRootProps())}
        transition="border .24s ease-in-out"
        border="2px dashed"
        borderColor={isDragActive ? "brand.500" : "brand.100"}
        borderRadius={8}
        p={6}
        textAlign="center"
        flex={1}
        width={"100%"}
        alignItems={"center"}
        justifyContent="center"
        flexDir={"column"}
        height={"100%"}
        minHeight={250}
        cursor={
          isValidating
            ? "not-allowed"
            : isUploading
            ? "progress"
            : file
            ? "default"
            : "pointer"
        }
      >
        {isUploading ? (
          <Flex
            flex={1}
            borderRadius={12}
            alignItems="center"
            justify={"center"}
            flexDir={"column"}
            gap={4}
            width="100%"
          >
            <Progress value={uploadProgress} width="100%" />
            <Text fontWeight={"bold"}>
              Uploading file: {uploadProgress}% complete
            </Text>
          </Flex>
        ) : isValidating ? (
          <Flex
            flex={1}
            borderRadius={12}
            alignItems="center"
            justify={"center"}
            flexDir={"column"}
            gap={4}
            width="100%"
          >
            <Progress value={validationProgress} width="100%" />
            <Text fontWeight={"bold"}>
              Validating file: {validationProgress}% complete
            </Text>
          </Flex>
        ) : !file ? (
          <>
            <input {...getInputProps()} />
            <Text
              fontSize={"sm"}
              fontWeight="medium"
              transition={"opacity .24s ease-in-out"}
              opacity={isDragActive ? 1 : 0}
              position={"absolute"}
              color={"brand.500"}
            >
              Drop your list here.
            </Text>
            <Text
              fontSize={"sm"}
              transition={"opacity .24s ease-in-out"}
              opacity={isDragActive ? 0 : 1}
              fontWeight="medium"
            >
              Drag & drop your list here, or click to select.
            </Text>
            <Text
              fontSize={"sm"}
              transition={"opacity .24s ease-in-out"}
              opacity={isDragActive ? 0 : 1}
              fontWeight="medium"
              color={"gray.500"}
            >
              (Only CSV files are allowed.)
            </Text>
          </>
        ) : (
          <Flex flexDir={"column"} gap={2}>
            <Flex
              alignItems={"center"}
              gap={1}
              p={8}
              direction="column"
              bgColor="gray.100"
              borderRadius={12}
            >
              <Flex gap={1} alignItems="center">
                <AttachmentIcon boxSize={4} color="gray.500" />
                <Text fontSize={"sm"} fontWeight="medium" color={"gray.700"}>
                  {file.name}
                </Text>
              </Flex>
              <Text>
                {file.size / 1024 / 1024 > 1
                  ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                  : `${(file.size / 1024).toFixed(2)} KB`}
              </Text>

              <Flex mt={2} gap={2} alignItems="center">
                <Checkbox
                  defaultChecked={isFileEncoded}
                  onChange={(e) => setIsFileEncoded(!e.target.checked)}
                  colorScheme="blue"
                >
                  <Flex gap={2} alignItems="center">
                    <Text>UUIDs are encoded (SHA256)</Text>
                    <Tooltip label="If the UUIDs in the file are already encoded, check this box.">
                      <InfoIcon color="gray.500" />
                    </Tooltip>
                  </Flex>
                </Checkbox>
              </Flex>
            </Flex>
            <Button
              bgColor={"brand.500"}
              color="white"
              _hover={{
                bgColor: "brand.600",
              }}
              onClick={() => handleUpload(file, isFileEncoded)}
              isLoading={isUploading}
              disabled={isUploading}
              loadingText="Uploading"
              mt={4}
            >
              Upload
            </Button>
            <Button
              variant={"ghost"}
              fontWeight="medium"
              onClick={() => {
                setFile(null);
                setValidationProgress(0);
                setUploadProgress(0);
                setIsValidating(false);
                setIsFileEncoded(false);
              }}
            >
              or select a different file
            </Button>
          </Flex>
        )}
      </Flex>
    );
  }, [
    file,
    getRootProps,
    getInputProps,
    isDragActive,
    isValidating,
    handleUpload,
    isUploading,
    validationProgress,
    uploadProgress,
  ]);

  const getChipColor = useCallback((status: string) => {
    switch (status) {
      case "CREATED":
        return "orange.500";
      case "UPLOADED":
        return "green.500";
      /* case "FAILED":
        return "red.500"; */
      default:
        return "gray.500";
    }
  }, []);

  return (
    <Flex flex={1} flexDir="column" gap={4}>
      <Flex w={"full"} justifyContent="space-between">
        <Heading fontSize={"xl"} alignSelf={"flex-start"}>
          Suppression List
        </Heading>
        <Button
          color={"white"}
          bgColor="brand.500"
          _hover={{
            bgColor: "brand.600",
          }}
          onClick={onOpen}
        >
          Upload List
        </Button>
      </Flex>
      <Flex flex={1} flexDir={"column"}>
        {suppressionsLoading ? (
          <Spinner alignSelf={"center"} justifySelf="center" />
        ) : (
          <Flex flexDir={"column"} gap={4}>
            {suppressions.length === 0 ? (
              <Text>No suppression lists found</Text>
            ) : (
              <Flex flexDir={"column"} gap={2}>
                {suppressions.map((suppression, index) => (
                  <Flex
                    key={`${index}-${suppression.filename}`}
                    p={4}
                    borderRadius={"md"}
                    alignItems={"center"}
                    gap={2}
                    transition={"background-color .24s ease-in-out"}
                    _hover={{
                      bgColor: "gray.200",
                    }}
                    justifyContent={"space-between"}
                  >
                    <Flex alignItems={"center"} gap={2}>
                      <Tag
                        bgColor={getChipColor(suppression.status)}
                        color="white"
                        borderRadius={8}
                        fontWeight={"bold"}
                      >
                        {suppression.status}
                      </Tag>
                      <Text>{suppression.filename}</Text>
                    </Flex>
                    <Flex>
                      <Text fontWeight={"medium"} color="gray.500">
                        {moment(suppression.createdAt).format("MMM DD, YYYY")}
                      </Text>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <ModalOverlay />
        <ModalContent minW={"50%"} minH="50%">
          <ModalHeader id="alert-dialog-title">
            Upload Suppression List
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody flex={1} p={6} display={"flex"}>
            <Flex
              flex={1}
              alignItems="center"
              justifyContent={"center"}
              flexDir="column"
              gap={4}
            >
              <Alert borderRadius={8} status="info">
                <AlertIcon />
                <Flex flexDir={"column"} gap={2}>
                  <Text fontWeight={"medium"}>
                    Ensure your CSV has a single column with all IDs listed, one
                    per row, like this:
                  </Text>
                  <Flex width={"100%"}>
                    <Flex
                      flexDir={"column"}
                      width="fit-content"
                      borderRightWidth={1}
                    >
                      <Code
                        textAlign={"center"}
                        py={1}
                        px={8}
                        borderBottomWidth={1}
                        fontWeight={"bold"}
                      >
                        (COLUMN HEADER)
                      </Code>
                      {EXAMPLE_UUIDS.map((uuid, index) => (
                        <Code
                          key={uuid}
                          textAlign={"center"}
                          py={1}
                          px={8}
                          width={72}
                          textOverflow="ellipsis"
                          noOfLines={1}
                          borderBottomWidth={
                            index === EXAMPLE_UUIDS.length - 1 ? 0 : 1
                          }
                        >
                          {uuid}
                        </Code>
                      ))}
                    </Flex>
                    <Flex
                      flex={1}
                      p={8}
                      textAlign="center"
                      bgColor={"gray.200"}
                      alignItems={"center"}
                    >
                      <Text>
                        <strong>Important:</strong> The list must have only one
                        column.
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Alert>
              {renderUploadView()}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default UploadSuppressionListView;
